import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Crosshair, Target, FileText, Brain, Shield, ChevronRight, 
  Check, Zap, Users, BarChart3, Clock, DollarSign, Star,
  ArrowRight, Menu, X, Flag
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

// Hero background image - Contractor Overwatch tactical command center
const HERO_BG = "https://customer-assets.emergentagent.com/job_estimator-ai/artifacts/qgri94kb_image.png";

// Feature icons mapping
const FeatureIcon = ({ type }) => {
  const icons = {
    intel: <Target className="h-8 w-8" />,
    weapon: <FileText className="h-8 w-8" />,
    brain: <Brain className="h-8 w-8" />,
  };
  return icons[type] || <Shield className="h-8 w-8" />;
};

export default function MarketingPage() {
  const [email, setEmail] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Check if user is logged in (initialize from localStorage)
  const [isLoggedIn] = useState(() => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    return !!token;
  });

  // Enable scrolling on this page by adding class to body
  useEffect(() => {
    document.body.classList.add("marketing-page");
    return () => {
      document.body.classList.remove("marketing-page");
    };
  }, []);

  const handleRequestAccess = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email address");
      return;
    }
    
    setSubmitting(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    toast.success("Access request received! We'll be in touch shortly.");
    setEmail("");
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-y-auto overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-lg border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo - Links to Dashboard */}
            <Link to="/dashboard" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center">
                <Crosshair className="h-5 w-5 text-emerald-400" />
              </div>
              <div>
                <span className="text-lg font-bold tracking-tight">CONTRACTOR</span>
                <span className="text-lg font-bold tracking-tight text-emerald-400"> OVERWATCH</span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm text-slate-300 hover:text-emerald-400 transition-colors">Features</a>
              <a href="#benefits" className="text-sm text-slate-300 hover:text-emerald-400 transition-colors">Benefits</a>
              <a href="#pricing" className="text-sm text-slate-300 hover:text-emerald-400 transition-colors">Pricing</a>
              <a href="#testimonials" className="text-sm text-slate-300 hover:text-emerald-400 transition-colors">Reviews</a>
              <Link to="/about" className="text-sm text-slate-300 hover:text-emerald-400 transition-colors">About</Link>
              <Link to={isLoggedIn ? "/dashboard" : "/login"}>
                <Button 
                  variant={isLoggedIn ? "default" : "outline"} 
                  size="sm" 
                  className={isLoggedIn 
                    ? "bg-emerald-600 hover:bg-emerald-500 text-white font-bold" 
                    : "border-slate-600 text-slate-300 hover:bg-slate-800"
                  }
                >
                  {isLoggedIn ? "OPEN APP" : "Sign In"}
                </Button>
              </Link>
              {!isLoggedIn && (
                <a href="#request-access">
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold">
                    REQUEST ACCESS
                  </Button>
                </a>
              )}
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2 text-slate-300"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-slate-900 border-t border-slate-800">
            <div className="px-4 py-4 space-y-3">
              <a href="#features" className="block text-slate-300 hover:text-emerald-400">Features</a>
              <a href="#benefits" className="block text-slate-300 hover:text-emerald-400">Benefits</a>
              <a href="#testimonials" className="block text-slate-300 hover:text-emerald-400">Reviews</a>
              <Link to="/about" className="block text-slate-300 hover:text-emerald-400">About</Link>
              <Link 
                to={isLoggedIn ? "/dashboard" : "/login"} 
                className="block text-slate-300 hover:text-emerald-400"
              >
                {isLoggedIn ? "OPEN APP" : "Sign In"}
              </Link>
              {!isLoggedIn && (
                <a href="#request-access">
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-500 mt-2">REQUEST ACCESS</Button>
                </a>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-950/70 to-slate-950" />
        
        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `linear-gradient(rgba(16, 185, 129, 0.1) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(16, 185, 129, 0.1) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }} />

        {/* Hero Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-20">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 mb-8">
            <Flag className="h-4 w-4 text-emerald-400" />
            <span className="text-sm font-semibold text-emerald-400 tracking-wide">VETERAN OWNED & OPERATED</span>
          </div>

          {/* Main Headline */}
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight mb-6 leading-none">
            <span className="block text-white" style={{ fontFamily: "'Impact', 'Arial Black', sans-serif", letterSpacing: '0.02em' }}>
              STOP GUESSING.
            </span>
            <span className="block text-emerald-400 mt-2" style={{ fontFamily: "'Impact', 'Arial Black', sans-serif", letterSpacing: '0.02em' }}>
              START COMMANDING.
            </span>
          </h1>

          {/* Sub-headline */}
          <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto mb-10 leading-relaxed">
            The first <span className="text-emerald-400 font-semibold">Military-Grade Operating System</span> for 
            Contractors who want to <span className="text-white font-semibold">dominate their market.</span>
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            {isLoggedIn ? (
              <Link to="/dashboard">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-lg px-8 py-6 shadow-lg shadow-emerald-900/50 group">
                  OPEN APP
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            ) : (
              <>
                <a href="#request-access">
                  <Button size="lg" className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-lg px-8 py-6 shadow-lg shadow-emerald-900/50 group">
                    REQUEST ACCESS
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </a>
                <Link to="/about">
                  <Button size="lg" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800 text-lg px-8 py-6">
                    MISSION ORIGIN
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Glass-morphism Dashboard Preview */}
          <div className="relative max-w-4xl mx-auto">
            <div className="absolute -inset-4 bg-emerald-500/20 blur-3xl rounded-full opacity-30" />
            <div className="relative bg-slate-900/80 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-2 shadow-2xl">
              <div className="bg-slate-950 rounded-xl overflow-hidden">
                {/* Mock Dashboard Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
                  <div className="flex items-center gap-2">
                    <Crosshair className="h-4 w-4 text-emerald-400" />
                    <span className="text-xs font-bold text-slate-300">COMMAND CENTER</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-red-500" />
                    <div className="w-2 h-2 rounded-full bg-amber-500" />
                    <div className="w-2 h-2 rounded-full bg-emerald-500" />
                  </div>
                </div>
                
                {/* Mock Dashboard Content */}
                <div className="p-4 grid grid-cols-4 gap-3">
                  {[
                    { label: "Active Ops", value: "12", color: "emerald" },
                    { label: "Revenue", value: "$847K", color: "emerald" },
                    { label: "Pipeline", value: "24", color: "amber" },
                    { label: "Close Rate", value: "78%", color: "emerald" },
                  ].map((stat, i) => (
                    <div key={i} className="bg-slate-800/50 rounded-lg p-3 text-center">
                      <div className={`text-lg font-bold text-${stat.color}-400`}>{stat.value}</div>
                      <div className="text-xs text-slate-500 uppercase tracking-wider">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-slate-600 flex items-start justify-center pt-2">
            <div className="w-1 h-2 bg-emerald-400 rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Feature Grid - The Full Manifest (12 Modules) */}
      <section id="features" className="py-24 bg-slate-950 relative">
        {/* Section Background Pattern */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(16, 185, 129, 0.5) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Section Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 border border-slate-700 mb-4">
              <Shield className="h-4 w-4 text-emerald-400" />
              <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">The Full Manifest</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-black text-white mb-4" style={{ fontFamily: "'Impact', 'Arial Black', sans-serif" }}>
              12 MODULES. ZERO WEAKNESS.
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              A complete operating system organized by mission phase. From first contact to final payment.
            </p>
          </div>

          {/* PHASE 1: INTELLIGENCE (Pre-Mission) */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-lg bg-cyan-500/20 border border-cyan-500/40 flex items-center justify-center">
                <span className="text-cyan-400 font-black text-sm">1</span>
              </div>
              <h3 className="text-lg font-bold text-cyan-400 uppercase tracking-wider">Phase 1: Intelligence</h3>
              <span className="text-xs text-slate-500 uppercase tracking-widest">(Pre-Mission)</span>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* THE BRAIN */}
              <div className="group bg-slate-900/80 border border-slate-800 rounded-xl p-5 hover:border-cyan-500/50 transition-all hover:bg-slate-900">
                <div className="w-10 h-10 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center mb-4 text-cyan-400 group-hover:scale-110 transition-transform">
                  <Brain className="h-5 w-5" />
                </div>
                <h4 className="text-base font-bold text-white mb-1">THE BRAIN</h4>
                <p className="text-cyan-400 text-xs font-semibold uppercase tracking-wider mb-2">Overwatch AI</p>
                <p className="text-slate-400 text-xs leading-relaxed">
                  Your 24/7 Tactical Advisor. Ask it about margins, weather risks, or lead decay. It analyzes your entire business.
                </p>
              </div>

              {/* THE TRAP */}
              <div className="group bg-slate-900/80 border border-slate-800 rounded-xl p-5 hover:border-cyan-500/50 transition-all hover:bg-slate-900">
                <div className="w-10 h-10 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center mb-4 text-cyan-400 group-hover:scale-110 transition-transform">
                  <Target className="h-5 w-5" />
                </div>
                <h4 className="text-base font-bold text-white mb-1">THE TRAP</h4>
                <p className="text-cyan-400 text-xs font-semibold uppercase tracking-wider mb-2">CRM Pipeline</p>
                <p className="text-slate-400 text-xs leading-relaxed">
                  Military-grade Kanban. Visual &ldquo;Rust&rdquo; indicators for stale leads. Auto-capture logic so no target escapes.
                </p>
              </div>

              {/* THE INTEL */}
              <div className="group bg-slate-900/80 border border-slate-800 rounded-xl p-5 hover:border-cyan-500/50 transition-all hover:bg-slate-900">
                <div className="w-10 h-10 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center mb-4 text-cyan-400 group-hover:scale-110 transition-transform">
                  <FileText className="h-5 w-5" />
                </div>
                <h4 className="text-base font-bold text-white mb-1">THE INTEL</h4>
                <p className="text-cyan-400 text-xs font-semibold uppercase tracking-wider mb-2">Blueprint Takeoff</p>
                <p className="text-slate-400 text-xs leading-relaxed">
                  Upload PDFs, measure square footage, and extract material lists in seconds. No more paper napkins.
                </p>
              </div>

              {/* RAPID FIRE */}
              <div className="group bg-slate-900/80 border border-slate-800 rounded-xl p-5 hover:border-cyan-500/50 transition-all hover:bg-slate-900">
                <div className="w-10 h-10 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center mb-4 text-cyan-400 group-hover:scale-110 transition-transform">
                  <Zap className="h-5 w-5" />
                </div>
                <h4 className="text-base font-bold text-white mb-1">RAPID FIRE</h4>
                <p className="text-cyan-400 text-xs font-semibold uppercase tracking-wider mb-2">Text-to-Estimate</p>
                <p className="text-slate-400 text-xs leading-relaxed">
                  No time to type? Text a rough scope to the AI (e.g., &ldquo;12x12 deck, composite&rdquo;), and it builds the line items for you.
                </p>
              </div>
            </div>
          </div>

          {/* PHASE 2: OPERATIONS (Active Mission) */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center">
                <span className="text-emerald-400 font-black text-sm">2</span>
              </div>
              <h3 className="text-lg font-bold text-emerald-400 uppercase tracking-wider">Phase 2: Operations</h3>
              <span className="text-xs text-slate-500 uppercase tracking-widest">(Active Mission)</span>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* THE SANDBOX */}
              <div className="group bg-slate-900/80 border border-slate-800 rounded-xl p-5 hover:border-emerald-500/50 transition-all hover:bg-slate-900">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mb-4 text-emerald-400 group-hover:scale-110 transition-transform">
                  <BarChart3 className="h-5 w-5" />
                </div>
                <h4 className="text-base font-bold text-white mb-1">THE SANDBOX</h4>
                <p className="text-emerald-400 text-xs font-semibold uppercase tracking-wider mb-2">Live Estimates</p>
                <p className="text-slate-400 text-xs leading-relaxed">
                  Crunch numbers safely. &ldquo;Blind Profit&rdquo; mode hides margins. Links every dollar to a specific client.
                </p>
              </div>

              {/* THE WEAPON */}
              <div className="group bg-slate-900/80 border border-slate-800 rounded-xl p-5 hover:border-emerald-500/50 transition-all hover:bg-slate-900">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mb-4 text-emerald-400 group-hover:scale-110 transition-transform">
                  <FileText className="h-5 w-5" />
                </div>
                <h4 className="text-base font-bold text-white mb-1">THE WEAPON</h4>
                <p className="text-emerald-400 text-xs font-semibold uppercase tracking-wider mb-2">SOW Generator</p>
                <p className="text-slate-400 text-xs leading-relaxed">
                  Generate dual documents: A &ldquo;Client Brief&rdquo; that sells the dream, and a redacted &ldquo;Operator Order&rdquo; for subs.
                </p>
              </div>

              {/* THE MATRIX */}
              <div className="group bg-slate-900/80 border border-slate-800 rounded-xl p-5 hover:border-emerald-500/50 transition-all hover:bg-slate-900">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mb-4 text-emerald-400 group-hover:scale-110 transition-transform">
                  <Users className="h-5 w-5" />
                </div>
                <h4 className="text-base font-bold text-white mb-1">THE MATRIX</h4>
                <p className="text-emerald-400 text-xs font-semibold uppercase tracking-wider mb-2">Sub Timeline</p>
                <p className="text-slate-400 text-xs leading-relaxed">
                  Squadron-level scheduling. See exactly where every sub is deployed across all jobs on a visual heatmap.
                </p>
              </div>

              {/* THE WATCH */}
              <div className="group bg-slate-900/80 border border-slate-800 rounded-xl p-5 hover:border-emerald-500/50 transition-all hover:bg-slate-900">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mb-4 text-emerald-400 group-hover:scale-110 transition-transform">
                  <Clock className="h-5 w-5" />
                </div>
                <h4 className="text-base font-bold text-white mb-1">THE WATCH</h4>
                <p className="text-emerald-400 text-xs font-semibold uppercase tracking-wider mb-2">GPS Timeclock</p>
                <p className="text-slate-400 text-xs leading-relaxed">
                  Geo-fenced time tracking. Crews clock in via mobile. You know exactly who is on site and when.
                </p>
              </div>
            </div>
          </div>

          {/* PHASE 3: EXECUTION (Field & Finance) */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-lg bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
                <span className="text-amber-400 font-black text-sm">3</span>
              </div>
              <h3 className="text-lg font-bold text-amber-400 uppercase tracking-wider">Phase 3: Execution</h3>
              <span className="text-xs text-slate-500 uppercase tracking-widest">(Field & Finance)</span>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* THE FIELD */}
              <div className="group bg-slate-900/80 border border-slate-800 rounded-xl p-5 hover:border-amber-500/50 transition-all hover:bg-slate-900">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-4 text-amber-400 group-hover:scale-110 transition-transform">
                  <Users className="h-5 w-5" />
                </div>
                <h4 className="text-base font-bold text-white mb-1">THE FIELD</h4>
                <p className="text-amber-400 text-xs font-semibold uppercase tracking-wider mb-2">Sub Portal</p>
                <p className="text-slate-400 text-xs leading-relaxed">
                  A dedicated mobile view for crews. They see the Docs, the Schedule, and Site Rules. They never see the money.
                </p>
              </div>

              {/* THE HARVEST */}
              <div className="group bg-slate-900/80 border border-slate-800 rounded-xl p-5 hover:border-amber-500/50 transition-all hover:bg-slate-900">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-4 text-amber-400 group-hover:scale-110 transition-transform">
                  <DollarSign className="h-5 w-5" />
                </div>
                <h4 className="text-base font-bold text-white mb-1">THE HARVEST</h4>
                <p className="text-amber-400 text-xs font-semibold uppercase tracking-wider mb-2">Invoicing</p>
                <p className="text-slate-400 text-xs leading-relaxed">
                  Turn estimates into invoices instantly. Track receivables, automate reminders, and capture payments.
                </p>
              </div>

              {/* THE SHIELD */}
              <div className="group bg-slate-900/80 border border-slate-800 rounded-xl p-5 hover:border-amber-500/50 transition-all hover:bg-slate-900">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-4 text-amber-400 group-hover:scale-110 transition-transform">
                  <Shield className="h-5 w-5" />
                </div>
                <h4 className="text-base font-bold text-white mb-1">THE SHIELD</h4>
                <p className="text-amber-400 text-xs font-semibold uppercase tracking-wider mb-2">Compliance</p>
                <p className="text-slate-400 text-xs leading-relaxed">
                  Auto-track sub insurance and licenses. The system locks them out if their GL policy is expired.
                </p>
              </div>

              {/* MOBILE COMMAND */}
              <div className="group bg-slate-900/80 border border-slate-800 rounded-xl p-5 hover:border-amber-500/50 transition-all hover:bg-slate-900">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-4 text-amber-400 group-hover:scale-110 transition-transform">
                  <Crosshair className="h-5 w-5" />
                </div>
                <h4 className="text-base font-bold text-white mb-1">MOBILE COMMAND</h4>
                <p className="text-amber-400 text-xs font-semibold uppercase tracking-wider mb-2">The App</p>
                <p className="text-slate-400 text-xs leading-relaxed">
                  Full OS access from your truck. Approve change orders, view site photos, and command the field remotely.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Benefit - Why Section */}
      <section id="benefits" className="py-24 bg-slate-900 relative overflow-hidden">
        {/* Background Accent */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-emerald-500/5 to-transparent" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 border border-slate-700 mb-6">
                <Zap className="h-4 w-4 text-emerald-400" />
                <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">The Why</span>
              </div>
              
              <h2 className="text-4xl sm:text-5xl font-black text-white mb-6 leading-tight" style={{ fontFamily: "'Impact', 'Arial Black', sans-serif" }}>
                CIVILIAN SOFTWARE IS <span className="text-red-500">SOFT.</span>
              </h2>
              
              <p className="text-xl text-slate-300 mb-8 leading-relaxed">
                Overwatch is built for the trenches. <span className="text-emerald-400 font-semibold">Track every dollar.</span>{" "}
                <span className="text-emerald-400 font-semibold">Command every sub.</span>{" "}
                <span className="text-emerald-400 font-semibold">Never lose a lead.</span>
              </p>

              <div className="space-y-4">
                {[
                  { icon: DollarSign, label: "Track Every Dollar", desc: "Real-time job costing with margin alerts" },
                  { icon: Users, label: "Command Every Sub", desc: "Work orders, schedules, and compliance in one view" },
                  { icon: Target, label: "Never Lose a Lead", desc: "Visual pipeline with decay tracking and auto follow-ups" },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                      <item.icon className="h-5 w-5 text-emerald-400" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white">{item.label}</h4>
                      <p className="text-sm text-slate-400">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right - Stats */}
            <div className="grid grid-cols-2 gap-6">
              {[
                { value: "47%", label: "Faster Estimates", icon: Clock },
                { value: "3.2x", label: "More Accurate Bids", icon: Target },
                { value: "$127K", label: "Avg Revenue Increase", icon: DollarSign },
                { value: "89%", label: "Client Retention", icon: Users },
              ].map((stat, i) => (
                <div key={i} className="bg-slate-950 border border-slate-800 rounded-2xl p-6 text-center hover:border-emerald-500/50 transition-colors">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto mb-4">
                    <stat.icon className="h-6 w-6 text-emerald-400" />
                  </div>
                  <div className="text-3xl font-black text-emerald-400 mb-1" style={{ fontFamily: "'Impact', 'Arial Black', sans-serif" }}>
                    {stat.value}
                  </div>
                  <div className="text-sm text-slate-400">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 border border-slate-700 mb-4">
              <Star className="h-4 w-4 text-amber-400" />
              <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Field Reports</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-black text-white mb-4" style={{ fontFamily: "'Impact', 'Arial Black', sans-serif" }}>
              FROM THE FRONT LINES
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                quote: "Went from losing bids to commanding a 68% close rate. This software thinks like a contractor.",
                name: "Mike Thompson",
                title: "Thompson Remodeling Co.",
                rating: 5
              },
              {
                quote: "Finally, software that doesn't treat me like I'm in an office. Built for guys who work with their hands.",
                name: "Jake Rodriguez",
                title: "Rodriguez Construction",
                rating: 5
              },
              {
                quote: "The AI SOW generator alone saved me 10 hours a week. Now my subs get clean orders and clients get pro proposals.",
                name: "Sarah Chen",
                title: "Chen Home Services",
                rating: 5
              }
            ].map((testimonial, i) => (
              <div key={i} className="bg-slate-900 border border-slate-800 rounded-2xl p-8 hover:border-slate-700 transition-colors">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, j) => (
                    <Star key={j} className="h-5 w-5 text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <p className="text-slate-300 mb-6 leading-relaxed italic">&ldquo;{testimonial.quote}&rdquo;</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <span className="text-emerald-400 font-bold">{testimonial.name[0]}</span>
                  </div>
                  <div>
                    <div className="font-bold text-white">{testimonial.name}</div>
                    <div className="text-sm text-slate-500">{testimonial.title}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-slate-900/50 relative">
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(16, 185, 129, 0.5) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 border border-slate-700 mb-4">
              <DollarSign className="h-4 w-4 text-emerald-400" />
              <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Pricing</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-black text-white mb-4" style={{ fontFamily: "'Impact', 'Arial Black', sans-serif" }}>
              ONE PLAN. FULL ARSENAL.
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              No confusing tiers. No feature gating. Get everything you need to run your contracting business like a pro.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {/* Main Plan */}
            <div className="bg-slate-900 border-2 border-emerald-500 rounded-2xl overflow-hidden relative">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 via-emerald-400 to-emerald-500" />
              
              <div className="p-8 sm:p-12">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
                  {/* Left - Plan Info */}
                  <div>
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 mb-4">
                      <Shield className="h-4 w-4 text-emerald-400" />
                      <span className="text-xs font-bold text-emerald-400 uppercase tracking-widest">Full Access</span>
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">Contractor Overwatch Pro</h3>
                    <p className="text-slate-400 mb-6">Everything you need to dominate your market</p>
                    
                    <div className="flex items-baseline gap-2 mb-6">
                      <span className="text-5xl font-black text-white" style={{ fontFamily: "'Impact', 'Arial Black', sans-serif" }}>$249</span>
                      <span className="text-slate-400">/month</span>
                    </div>

                    <Link to="/login">
                      <Button size="lg" className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-8 w-full sm:w-auto">
                        START YOUR MISSION <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    </Link>
                  </div>

                  {/* Right - Features */}
                  <div className="lg:w-1/2">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {[
                        "AI-Powered Estimating",
                        "Tactical Proposal Builder",
                        "CRM Pipeline & Lead Tracking",
                        "Blueprint Takeoff",
                        "Job Costing & Margins",
                        "Subcontractor Management",
                        "Client Portal & E-Signatures",
                        "AI Receipt Scanning",
                        "Invoicing & Payments",
                        "Real-time Analytics",
                        "Unlimited Users",
                        "Priority Support"
                      ].map((feature, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                          <span className="text-sm text-slate-300">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Add-on */}
            <div className="mt-6 bg-slate-900/80 border border-slate-800 rounded-xl p-6 hover:border-amber-500/50 transition-colors">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
                    <BarChart3 className="h-6 w-6 text-amber-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-bold text-white">Plaid Banking Integration</h4>
                      <span className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 text-xs font-bold uppercase">Add-On</span>
                    </div>
                    <p className="text-sm text-slate-400">
                      Connect your bank accounts for automatic transaction imports, smart reconciliation, and real-time cash flow tracking.
                    </p>
                  </div>
                </div>
                <div className="flex items-baseline gap-2 flex-shrink-0 sm:text-right">
                  <span className="text-2xl font-black text-amber-400">+$100</span>
                  <span className="text-slate-500">/month</span>
                </div>
              </div>
            </div>

            {/* Money Back Guarantee */}
            <div className="mt-8 text-center">
              <div className="inline-flex items-center gap-2 text-slate-400">
                <Shield className="h-5 w-5 text-emerald-400" />
                <span className="text-sm">30-day money-back guarantee. No questions asked.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="request-access" className="py-24 bg-gradient-to-b from-slate-900 to-slate-950 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-emerald-500/20 rounded-full blur-3xl" />
        </div>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 mb-6">
            <Crosshair className="h-4 w-4 text-emerald-400" />
            <span className="text-xs font-bold text-emerald-400 uppercase tracking-widest">Join The Mission</span>
          </div>

          <h2 className="text-4xl sm:text-5xl font-black text-white mb-6" style={{ fontFamily: "'Impact', 'Arial Black', sans-serif" }}>
            READY TO TAKE COMMAND?
          </h2>
          
          <p className="text-xl text-slate-300 mb-10">
            Request early access and be among the first contractors to deploy Overwatch.
          </p>

          <form onSubmit={handleRequestAccess} className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
            <Input
              type="email"
              placeholder="Enter your email address..."
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 py-6 text-lg"
            />
            <Button 
              type="submit"
              size="lg"
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-8 py-6 text-lg shadow-lg shadow-emerald-900/50"
            >
              {submitting ? "Submitting..." : "REQUEST ACCESS"}
            </Button>
          </form>

          <p className="text-sm text-slate-500 mt-6">
            No credit card required. Early access is free for founding members.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-950 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Link to="/dashboard" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center">
                <Crosshair className="h-4 w-4 text-emerald-400" />
              </div>
              <span className="font-bold text-slate-400">CONTRACTOR <span className="text-emerald-400">OVERWATCH</span></span>
            </Link>
            
            <div className="flex items-center gap-6 text-sm text-slate-500">
              <Link to="/about" className="hover:text-emerald-400 transition-colors">About</Link>
            </div>
            
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Flag className="h-4 w-4 text-red-500" />
              <span>Veteran Owned & Operated</span>
            </div>

            <div className="text-sm text-slate-500">
              © {new Date().getFullYear()} Contractor Overwatch. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
